// ============================================
// VISTAPUBLICAPRODUCTO.JS — Página pública de un link compartido
// ============================================
// Renderizada por App.js en /p/:codigo y /v/:codigo SIN autenticación.
// Mobile-first. Muestra producto único o vitrina con productos múltiples.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from './config/api';

const PP = "'Poppins', sans-serif";
const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const COLOR_PRIMARIO = '#185FA5';
const COLOR_VERDE = '#16a34a';
const COLOR_OSCURO = '#1a3a5c';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_FONDO = '#fafbfc';

function fmtMXN(n) {
  return `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VistaPublicaProducto() {
  const { codigo } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [imagenActiva, setImagenActiva] = useState(0);

  useEffect(() => {
    document.title = 'Beep México';
    fetch(`${API}/api/compartir/v/${encodeURIComponent(codigo)}`)
      .then(x => x.json())
      .then(r => {
        if (!r.ok) throw new Error(r.error || 'no encontrado');
        setData(r);
        if (r.titulo) document.title = `${r.titulo} · Beep México`;
      })
      .catch(e => setError(e.message))
      .finally(() => setCargando(false));
  }, [codigo]);

  if (cargando) {
    return (
      <Layout>
        <div style={{ padding: 60, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
          Cargando…
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_OSCURO }}>
            Este link no está disponible
          </div>
          <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, marginTop: 8 }}>
            {error || 'Quizá el link expiró o fue desactivado.'}
          </div>
          <a
            href="https://www.beepmexico.com"
            style={{ marginTop: 20, display: 'inline-block', color: COLOR_PRIMARIO, fontWeight: 600 }}
          >Visitar beepmexico.com →</a>
        </div>
      </Layout>
    );
  }

  const productos = Array.isArray(data.productos) ? data.productos : [];
  const esVitrina = data.tipo === 'vitrina';
  const productoUnico = !esVitrina && productos.length > 0 ? productos[0] : null;

  // Galería del producto único (img_portada + datos_completos.imagenes si hay)
  const galeria = (() => {
    if (!productoUnico) return [];
    const set = new Set();
    if (productoUnico.imagen_url) set.add(productoUnico.imagen_url);
    const dc = productoUnico.datos_completos || {};
    const extra = Array.isArray(dc.imagenes) ? dc.imagenes
      : (Array.isArray(dc.fotos) ? dc.fotos : []);
    for (const i of extra) {
      if (typeof i === 'string') set.add(i);
      else if (i && i.imagen) set.add(i.imagen);
      else if (i && i.url) set.add(i.url);
    }
    return [...set].slice(0, 6);
  })();

  return (
    <Layout>
      <div style={{ padding: '24px 16px', maxWidth: 720, margin: '0 auto', fontFamily: PP, color: COLOR_GRIS }}>
        {data.mensaje_vendedor && (
          <div style={{
            background: '#fff', borderRadius: 12, padding: 16, marginBottom: 18,
            border: `1px solid #e6e8ea`, fontSize: 14, lineHeight: 1.5,
          }}>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
              💬 Mensaje del vendedor
            </div>
            <div style={{ color: COLOR_OSCURO, fontWeight: 500 }}>{data.mensaje_vendedor}</div>
          </div>
        )}

        {/* PRODUCTO ÚNICO */}
        {productoUnico && (
          <ProductoUnicoView
            producto={productoUnico}
            galeria={galeria}
            imagenActiva={imagenActiva}
            setImagenActiva={setImagenActiva}
            esPactado={data.es_pactado}
          />
        )}

        {/* VITRINA */}
        {esVitrina && (
          <VitrinaView productos={productos} totalMXN={data.total_mxn} />
        )}

        {/* Contacto */}
        <Contacto data={data} url={typeof window !== 'undefined' ? window.location.href : ''} />

        {/* Footer */}
        <div style={{
          textAlign: 'center', marginTop: 30, paddingTop: 16,
          borderTop: `1px solid #e6e8ea`, fontSize: 11, color: COLOR_GRIS_CLARO,
          lineHeight: 1.6,
        }}>
          <div>
            {data.empresa?.nombre || 'Beep México'} · {data.empresa?.web || 'www.beepmexico.com'}
          </div>
          <div style={{ marginTop: 6, fontStyle: 'italic' }}>
            Precios sujetos a variación del tipo de cambio. Vigentes mientras el TC se mantenga estable.
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ============================================
// LAYOUT con header (logo Beep)
// ============================================
function Layout({ children }) {
  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh',
      background: COLOR_FONDO, fontFamily: PP, color: COLOR_GRIS,
    }}>
      <header style={{
        background: '#fff', padding: '16px 16px 12px',
        borderBottom: `1px solid #e6e8ea`,
        textAlign: 'center',
      }}>
        <img
          src="/Logo Horizontal Original.png" alt="Beep México"
          style={{ height: 48, maxWidth: '60%', objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Soluciones en seguridad electrónica
        </div>
      </header>
      {children}
    </div>
  );
}

// ============================================
// Vista de un solo producto
// ============================================
function ProductoUnicoView({ producto, galeria, imagenActiva, setImagenActiva, esPactado }) {
  const fotoActual = galeria[imagenActiva] || producto.imagen_url || null;
  const dc = producto.datos_completos || {};
  const features = extraerFeatures(producto, dc);

  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid #e6e8ea` }}>
      {/* Galería */}
      <div style={{
        background: '#fff', padding: 16, textAlign: 'center',
        borderBottom: galeria.length > 1 ? `1px solid #e6e8ea` : 'none',
      }}>
        <div style={{
          width: '100%', aspectRatio: '1 / 1', maxHeight: 480, background: COLOR_FONDO,
          borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', fontSize: 80, color: COLOR_GRIS_CLARO,
        }}>
          {fotoActual ? (
            <img
              src={fotoActual} alt={producto.nombre} loading="lazy"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : '📦'}
        </div>
        {galeria.length > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            {galeria.map((g, i) => (
              <button
                key={i}
                onClick={() => setImagenActiva(i)}
                style={{
                  width: 56, height: 56, borderRadius: 8, padding: 4,
                  border: `2px solid ${i === imagenActiva ? COLOR_PRIMARIO : '#e6e8ea'}`,
                  background: COLOR_FONDO, cursor: 'pointer', overflow: 'hidden',
                }}
              >
                <img
                  src={g} alt="" loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: 20 }}>
        {producto.marca && (
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO,
            textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8,
          }}>{producto.marca}</div>
        )}
        <div style={{
          fontSize: 22, fontWeight: 700, color: COLOR_OSCURO,
          lineHeight: 1.3, marginBottom: 6,
        }}>{producto.nombre}</div>
        {producto.modelo && (
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 14 }}>
            Modelo: <strong style={{ color: COLOR_GRIS }}>{producto.modelo}</strong>
          </div>
        )}

        <div style={{
          fontSize: 30, fontWeight: 700, color: COLOR_OSCURO,
          margin: '14px 0 4px',
        }}>
          {fmtMXN(producto.precio_mxn)} <span style={{ fontSize: 14, color: COLOR_GRIS_CLARO }}>MXN</span>
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 14 }}>
          IVA incluido
        </div>

        {esPactado && (
          <div style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: 99,
            background: '#e8faf5', color: COLOR_VERDE, fontSize: 11, fontWeight: 700,
            marginBottom: 14,
          }}>✓ Precio especial para tu cuenta</div>
        )}

        {features.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: COLOR_OSCURO,
              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8,
            }}>Características</div>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.8 }}>
              {features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}

        {dc.garantia && (
          <div style={{ marginTop: 14, fontSize: 13, color: COLOR_GRIS }}>
            <strong>Garantía:</strong> {dc.garantia}
          </div>
        )}
      </div>
    </div>
  );
}

// Heurística simple para mostrar bullets
function extraerFeatures(producto, dc) {
  const out = [];
  if (dc.descripcion_corta && typeof dc.descripcion_corta === 'string') {
    // separa por líneas o por punto+espacio
    const split = dc.descripcion_corta.split(/[•\n]+/).map(s => s.trim()).filter(Boolean);
    if (split.length >= 2) return split.slice(0, 6);
  }
  if (dc.garantia) out.push(`Garantía: ${dc.garantia}`);
  if (producto.unidad && producto.unidad !== 'pieza') out.push(`Unidad: ${producto.unidad}`);
  if (dc.peso) out.push(`Peso: ${dc.peso} kg`);
  if (dc.alto || dc.ancho || dc.largo) {
    out.push(`Dimensiones: ${dc.largo || '—'}×${dc.ancho || '—'}×${dc.alto || '—'} cm`);
  }
  return out;
}

// ============================================
// Vista vitrina (múltiples productos)
// ============================================
function VitrinaView({ productos, totalMXN }) {
  return (
    <div>
      <div style={{
        fontSize: 12, color: COLOR_GRIS_CLARO,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12,
      }}>Productos cotizados</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {productos.map((p, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 12, padding: 14, border: `1px solid #e6e8ea`,
            display: 'flex', gap: 12,
          }}>
            <div style={{
              width: 80, height: 80, flexShrink: 0,
              background: COLOR_FONDO, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', fontSize: 28, color: COLOR_GRIS_CLARO,
            }}>
              {p.imagen_url ? (
                <img
                  src={p.imagen_url} alt="" loading="lazy"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : '📦'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {p.marca && (
                <div style={{ fontSize: 10, fontWeight: 700, color: COLOR_GRIS_CLARO, textTransform: 'uppercase' }}>
                  {p.marca}
                </div>
              )}
              <div style={{
                fontSize: 14, fontWeight: 600, color: COLOR_OSCURO,
                lineHeight: 1.3,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{p.nombre}</div>
              <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                Cantidad: <strong>{Number(p.cantidad || 1)}</strong> {p.unidad || ''}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginTop: 6 }}>
                {fmtMXN(p.subtotal_mxn != null ? p.subtotal_mxn : (Number(p.precio_mxn || 0) * Number(p.cantidad || 1)))}
                <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 500, marginLeft: 6 }}>
                  ({fmtMXN(p.precio_mxn)} c/u)
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 16, padding: 16, background: '#fff', borderRadius: 12,
        border: `2px solid ${COLOR_PRIMARIO}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_GRIS }}>Total estimado</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: COLOR_PRIMARIO }}>
          {fmtMXN(totalMXN)} <span style={{ fontSize: 14, color: COLOR_GRIS_CLARO }}>MXN</span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 8, textAlign: 'center' }}>
        IVA incluido. Sujeto a confirmación final.
      </div>
    </div>
  );
}

// ============================================
// Botones de contacto
// ============================================
function Contacto({ data, url }) {
  const v = data.vendedor || {};
  const titulo = data.titulo || 'producto';

  const whatsappURL = (() => {
    if (!v.whatsapp) return null;
    const num = String(v.whatsapp).replace(/\D/g, '');
    if (!num) return null;
    const msg = encodeURIComponent(`Hola, vi este ${data.tipo === 'vitrina' ? 'cotización' : 'producto'} (${titulo}). Me interesa: ${url}`);
    return `https://wa.me/${num}?text=${msg}`;
  })();
  const correoURL = v.correo
    ? `mailto:${v.correo}?subject=${encodeURIComponent('Consulta: ' + titulo)}&body=${encodeURIComponent('Hola, vi tu cotización: ' + url)}`
    : null;

  if (!whatsappURL && !correoURL) return null;

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 18, marginTop: 18,
      border: `1px solid #e6e8ea`, textAlign: 'center',
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
        ¿Te interesa? Contáctanos
      </div>
      {v.nombre && (
        <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, marginBottom: 14 }}>
          {v.nombre}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {whatsappURL && (
          <a
            href={whatsappURL} target="_blank" rel="noopener noreferrer"
            style={{
              padding: '12px 20px', borderRadius: 10, background: '#25D366', color: '#fff',
              fontFamily: PP, fontSize: 14, fontWeight: 700, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 140, justifyContent: 'center',
            }}
          >💬 WhatsApp</a>
        )}
        {correoURL && (
          <a
            href={correoURL}
            style={{
              padding: '12px 20px', borderRadius: 10, background: COLOR_PRIMARIO, color: '#fff',
              fontFamily: PP, fontSize: 14, fontWeight: 700, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 140, justifyContent: 'center',
            }}
          >✉️ Correo</a>
        )}
      </div>
    </div>
  );
}
